import * as React from 'react';
import { css } from '@emotion/core';
import Modal from '../layout/modal/Modal';
import ModalHeader from '../layout/modal/ModalHeader';
import { useHistory } from 'react-router-dom';
import { useMsgText } from '../localization/hooks';
import { Str } from '../localization/translationKeys';
import LocalDiv from '../localization/LocalDiv';
import { FLEX_COLUMN, FLEX_ROW } from '../layout/flexBox';
import { COLORS } from '../globalStyle/colors';
import { btnStyle } from '../forms/styles/button';
import { onMobileDevice } from '../globalStyle/mediaQueries';
import Spacer from '../layout/Spacer';
import LocalInput from '../localization/LocalInput';
import { INPUT } from '../forms/styles/input';
import LocalBtn from '../localization/LocalBtn';
import { ERROR_MSG } from '../forms/styles/messages';
import { updatePasswordFormValidation } from '../auth/formValidations';
import { useFormik } from 'formik';
import { useAppState } from '../core/redux/hooks';
import { useError } from '../core/api/hooks';
import { ActionType } from '../core/redux/actionType';
import { useDispatch } from 'react-redux';
import { useMount } from '../core/hooks/useMount';
import { passwordRequirementMessageAction } from '../auth/accountSetup/api/passwordRequirementMessageAction';
import { newPasswordAction } from '../auth/accountSetup/api/newPasswordAction';


const BTN_STYLE = css`
  min-width: 140px;
`;

const styles = {
  modal:        [
    FLEX_COLUMN,
    css`
      min-height: 312px;
      
      ${onMobileDevice}  {
        height: 100vh;
      }
    `,
  ],
  root:         [
    FLEX_COLUMN,
    css`
      flex-grow: 1;
      align-items: stretch;
      justify-content: flex-end;
      margin: 30px auto;
      max-width: 300px;
    `,
  ],
  pwInput:      [
    css`
      margin-bottom: 5px;
    `,
  ],
  message:      [
    css`
      margin-bottom: 15px;
    `,
  ],
  buttonRow:    [
    FLEX_ROW,
    css`
      margin: 20px 16px;
      justify-content: flex-end;
    `,
  ],
  submitButton: [
    btnStyle.primary(COLORS.BLUE),
    BTN_STYLE,
  ],
  cancelButton: [
    btnStyle.secondary(COLORS.BLUE),
    BTN_STYLE,
    css`
      background-color: transparent;
    `,
  ],
};


type Props = {
  className?: string;
};


/**
 * ChangePasswordModal: a component that allows a user to update the password.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ChangePasswordModal: React.FC<Props> = props => {
  const dispatch = useDispatch();

  useMount(() => { dispatch(passwordRequirementMessageAction);});

  const createPassword = React.useCallback(
    (passwordHash: string) => dispatch(newPasswordAction(passwordHash)),
    [dispatch],
  );

  const title = useMsgText(Str.ChangePasswordTitle);

  const pwReqMsg    = useAppState(state => state.auth.passwordRequirementMessage);
  const minPwLength = useAppState(state => state.auth.currentUser?.passwordRequirements.passwordLength);

  const history = useHistory();
  const dismiss = React.useCallback(() => history.goBack(), [history]);

  const invalidCurrentPw = useMsgText(Str.IncorrectPassword);
  const pwReqsNotMet     = useMsgText(Str.PasswordFailsRequirements);
  const pwsDoNotMatch    = useMsgText(Str.PasswordMismatch);
  const validationError  = !!useError(ActionType.NewPasswordRequest);
  const password         = useAppState(state => state.auth.currentUser?.passwordUsed);

  const { handleSubmit, handleChange, isValid, errors, values } = useFormik({
    initialValues:    {
      currentPassword: '',
      password:        '',
      confirmPassword: '',
    },
    onSubmit:         (values) => {
      createPassword(values.password);
      dismiss();
    },
    validationSchema: updatePasswordFormValidation(
      `${password}`,
      invalidCurrentPw,
      pwReqsNotMet,
      pwsDoNotMatch,
      minPwLength,
    ),
    validateOnChange: false,
  });

  return (
    <Modal onDismiss={dismiss} dismissable={true} css={styles.modal}>
      <fieldset
        disabled={validationError}
        css={[
          css`
            border: 0 none; // Chrome adds padding and margins to fieldsets by default
            margin: 0;
            padding: 0;
            max-width: 550px;
          `,
        ]}
      >
        <form onSubmit={handleSubmit}>
          <ModalHeader title={title} onDismiss={dismiss} />
          <LocalDiv css={styles.root}>
            <LocalDiv css={styles.message}>
              {pwReqMsg}
            </LocalDiv>
            {
              (validationError || !isValid) && (
                <div
                  css={[
                    FLEX_ROW,
                    ERROR_MSG,
                    css`
                    align-items: center;
                  `,
                  ]}
                >
                  {
                    validationError
                      ? `${pwReqsNotMet}`
                      : `${errors.currentPassword ?? errors.password ?? errors.confirmPassword}`
                  }
                </div>
              )
            }
            <LocalInput
              placeholderTranslation={Str.CurrentPassword}
              name='currentPassword'
              type='password'
              value={values.currentPassword}
              css={[
                INPUT(),
                styles.pwInput,
              ]}
              onChange={handleChange}
              autoComplete='off'
            />
            <LocalInput
              placeholderTranslation={Str.NewPassword}
              name='password'
              type='password'
              value={values.password}
              css={[
                INPUT(),
                styles.pwInput,
              ]}
              onChange={handleChange}
              autoComplete='off'
            />
            <LocalInput
              placeholderTranslation={Str.ConfirmPassword}
              name='confirmPassword'
              type='password'
              value={values.confirmPassword}
              css={[
                INPUT(),
                styles.pwInput,
              ]}
              onChange={handleChange}
              autoComplete='off'
            />
          </LocalDiv>
          <LocalDiv css={styles.buttonRow}>
            <LocalBtn onClick={dismiss} css={styles.cancelButton} str={Str.Cancel} />
            <Spacer sizePx={12} />
            <LocalBtn type='submit' css={styles.submitButton} str={Str.Save} />
          </LocalDiv>
        </form>
      </fieldset>
    </Modal>
  );
};

export default ChangePasswordModal;
